import { CheckCircle2, FileEdit, Mail, Send, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QuotationStatus } from '../types/quotation.types';

// CSC-288: icon-only status chip — colour-coded square with the status's
// lucide icon centred. Used in the workspace's master list (compact 20×20)
// and detail-panel header (larger). Default size matches the master list;
// callers can override via className for other contexts. The full status
// label is exposed via title + aria-label so screen readers + tooltips
// still surface it.

const STATUS_VISUALS: Record<
  QuotationStatus,
  { icon: typeof FileEdit; label: string; className: string; iconClassName?: string }
> = {
  [QuotationStatus.Draft]: {
    icon: FileEdit,
    label: 'Draft',
    className: 'bg-muted text-muted-foreground',
  },
  [QuotationStatus.AutoDraft]: {
    icon: Mail,
    label: 'Auto-draft (from email)',
    className: 'bg-info/15 text-info',
  },
  [QuotationStatus.Submitted]: {
    icon: CheckCircle2,
    label: 'Submitted to NetSuite',
    className: 'bg-success/15 text-success',
  },
  [QuotationStatus.Sent]: {
    icon: Send,
    // "Sent" is a distinct lifecycle state, not a semantic status — keep it a
    // categorical purple (with a dark-mode variant) rather than mapping it to a
    // semantic token. highlight/destructive are red and would misread as an error.
    label: 'Sent to client',
    className: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  },
};

// 86d3mnuxe — outbox posting overlays (the posting lifecycle moved off the entity).
const POSTING_VISUAL = {
  icon: Loader2, label: 'Posting to NetSuite…', className: 'bg-info/15 text-info',
  iconClassName: 'animate-spin' as string | undefined,
};
const FAILED_VISUAL = {
  icon: AlertTriangle, label: 'Posting failed — open to retry', className: 'bg-destructive/15 text-destructive',
  iconClassName: undefined as string | undefined,
};

interface Props {
  status: QuotationStatus;
  /** 86d3mnuxe — outbox posting overlay ("Posting"/"Failed") for a quotation with a posting.cl job. */
  postingStatus?: 'Posting' | 'Failed' | null;
  /** Override the wrapper sizing. Default `h-5 w-5` (20×20). */
  className?: string;
  /** Override the inner icon sizing. Default `h-3 w-3`. */
  iconClassName?: string;
}

export function QuotationStatusIcon({ status, postingStatus, className, iconClassName }: Props) {
  const v =
    postingStatus === 'Posting' ? POSTING_VISUAL : postingStatus === 'Failed' ? FAILED_VISUAL : STATUS_VISUALS[status];
  const Icon = v.icon;
  return (
    <span
      title={v.label}
      aria-label={v.label}
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded',
        v.className,
        className,
      )}
    >
      <Icon className={cn('h-3 w-3', v.iconClassName, iconClassName)} />
    </span>
  );
}
